// Showing untrusted file contents (repository files, READMEs): code highlighted by
// highlight.js (which escapes its input), Markdown rendered with raw HTML switched off and
// the result sanitised by DOMPurify. Loaded on demand, so the app doesn't carry them
// until someone opens a repository.

const LANG_BY_EXT: Record<string, string> = {
  rs: 'rust',
  ts: 'typescript',
  tsx: 'typescript',
  js: 'javascript',
  jsx: 'javascript',
  mjs: 'javascript',
  vue: 'xml',
  html: 'xml',
  xml: 'xml',
  svg: 'xml',
  css: 'css',
  scss: 'scss',
  json: 'json',
  toml: 'ini',
  ini: 'ini',
  yml: 'yaml',
  yaml: 'yaml',
  md: 'markdown',
  py: 'python',
  rb: 'ruby',
  go: 'go',
  java: 'java',
  kt: 'kotlin',
  swift: 'swift',
  c: 'c',
  h: 'c',
  cc: 'cpp',
  cpp: 'cpp',
  hpp: 'cpp',
  cs: 'csharp',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  fish: 'bash',
  sql: 'sql',
  lua: 'lua',
  php: 'php',
  dockerfile: 'dockerfile',
  makefile: 'makefile',
}

/** highlight.js's language for a file name, if it knows one. */
export function languageOf(name: string): string | undefined {
  const lower = name.toLowerCase()
  if (lower === 'dockerfile' || lower === 'makefile') return LANG_BY_EXT[lower]
  return LANG_BY_EXT[lower.split('.').pop() ?? '']
}

/** Code as highlighted HTML lines (escaped); plain escaped lines if the language is unknown. */
export async function highlightLines(code: string, name: string): Promise<string[]> {
  const hljs = (await import('highlight.js/lib/common')).default
  const lang = languageOf(name)
  const html = lang && hljs.getLanguage(lang) ? hljs.highlight(code, { language: lang, ignoreIllegals: true }).value : escapeHtml(code)
  return splitHighlighted(html)
}

/**
 * Splits highlighted HTML into lines, closing and reopening spans that cross line ends so
 * each line is well-formed on its own.
 */
export function splitHighlighted(html: string): string[] {
  const lines: string[] = []
  const open: string[] = []
  let cur = ''
  const re = /(<span[^>]*>)|(<\/span>)|(\n)|([^<\n]+|<)/g
  for (const m of html.matchAll(re)) {
    if (m[1]) {
      open.push(m[1])
      cur += m[1]
    } else if (m[2]) {
      open.pop()
      cur += m[2]
    } else if (m[3]) {
      lines.push(cur + '</span>'.repeat(open.length))
      cur = open.join('')
    } else {
      cur += m[4]
    }
  }
  lines.push(cur + '</span>'.repeat(open.length))
  if (lines.length > 1 && lines.at(-1) === '') lines.pop()
  return lines
}

export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** Markdown as safe HTML: no raw HTML or images, sanitised, links opening outside the app. */
export async function renderMarkdown(md: string): Promise<string> {
  const [{ default: MarkdownIt }, { default: DOMPurify }] = await Promise.all([import('markdown-it'), import('dompurify')])
  const html = new MarkdownIt({ html: false, linkify: true }).render(md)
  // No images: one pointing elsewhere would tell another server who read this, and when.
  const clean = DOMPurify.sanitize(html, { USE_PROFILES: { html: true }, FORBID_TAGS: ['img', 'style'], FORBID_ATTR: ['style'] })
  // Links leave the app in a new tab, without telling the other site where they came from.
  return clean.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer nofollow" ')
}
